import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { markGuideDone, readGuideProgress, saveGuideProgress } from "./guides";
import { SpotlightOverlay } from "./SpotlightOverlay";

// Small step engine for multi-screen guides. A step is
//   { target, message, capture?, waitMs? }
// target: CSS selector, or (ctx) => selector. The user performs the step by clicking the highlighted
// element, which advances to the next one. capture(el) can return values stored in ctx for later steps.
// Finishing a guide (auto or replayed from the menu) marks it done.
export function useGuidedTour({ guideKey, steps, onAbort, onFinish, busy = false }) {
  const [index, setIndex] = useState(-1);
  const [ready, setReady] = useState(false);
  const [run, setRun] = useState(0);
  const ctxRef = useRef({});
  const runningRef = useRef(false);
  const step = steps[index];
  const busyRef = useRef(busy); // while true (page loading) the tour waits instead of showing or giving up
  busyRef.current = busy;

  const finish = useCallback(
    (done) => {
      runningRef.current = false;
      setIndex(-1);
      setReady(false);
      saveGuideProgress(guideKey, { paused: !done });
      if (done) markGuideDone(guideKey);
      if (done && onFinish) onFinish();
    },
    [guideKey, onFinish]
  );

  const start = useCallback((manual = false) => {
    runningRef.current = true;
    setRun((value) => value + 1);
    const saved = readGuideProgress(guideKey);
    ctxRef.current = saved.ctx || {};
    setReady(false);
    setIndex(manual ? 0 : Math.min(saved.index || 0, steps.length - 1));
    saveGuideProgress(guideKey, { paused: false });
  }, [guideKey, steps.length]);

  const next = useCallback(() => {
    if (!runningRef.current) return;
    setReady(false);
    if (index + 1 >= steps.length) finish(true);
    else setIndex(index + 1);
  }, [index, steps.length, finish]);

  useEffect(() => () => { runningRef.current = false; }, []);

  useEffect(() => {
    const onToggle = () => {
      if (!runningRef.current) return;
      setReady(false);
      setRun((value) => value + 1);
    };
    window.addEventListener("pageInfoModalToggle", onToggle);
    return () => window.removeEventListener("pageInfoModalToggle", onToggle);
  }, []);

  useEffect(() => {
    if (index >= 0) saveGuideProgress(guideKey, { index, ctx: ctxRef.current });
  }, [guideKey, index]);

  // Wait for the step's target (it may sit in a modal that is still opening); give up if it never shows.
  useEffect(() => {
    if (!step) return undefined;
    const selector = typeof step.target === "function" ? step.target(ctxRef.current) : step.target;
    const deadline = Date.now() + (step.waitMs ?? 3000);
    let deadline2 = deadline;
    const timer = setInterval(() => {
      if (busyRef.current || window.isPageInfoModalScheduled || window.isPageInfoModalOpen) {
        deadline2 = Date.now() + (step.waitMs ?? 3000);
        setReady(false);
        return;
      }
      if (step.skipWhen?.()) {
        clearInterval(timer);
        if (index + 1 >= steps.length) finish(true);
        else setIndex(index + 1);
        return;
      }
      const el = document.querySelector(selector);
      if (el) {
        clearInterval(timer);
        if (step.isDone?.(el)) {
          if (index + 1 >= steps.length) finish(true);
          else setIndex(index + 1);
          return;
        }
        if (step.capture) ctxRef.current = { ...ctxRef.current, ...step.capture(el) };
        saveGuideProgress(guideKey, { index, ctx: ctxRef.current });
        setReady(true);
      } else if (Date.now() > deadline2) {
        clearInterval(timer);
        finish(false);
        if (onAbort) onAbort(index);
      }
    }, 150);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, run]);

  const selector = step ? (typeof step.target === "function" ? step.target(ctxRef.current) : step.target) : null;

  return {
    active: index >= 0,
    ready: ready && !!step,
    step,
    selector,
    index,
    total: steps.length,
    start,
    next,
    skip: () => { finish(false); markGuideDone(guideKey); },
    complete: () => finish(true),
    dismiss: () => finish(false),
  };
}

export const GuideSpotlight = ({ tour }) => {
  const history = useHistory();
  if (!tour.ready) return null;
  const last = tour.index === tour.total - 1;
  const info = !!tour.step.info; // read-only step: advance with the Next button, not by clicking the target
  const action = tour.step.action?.();
  if (action) {
    return (
      <SpotlightOverlay
        targetSelector={tour.selector}
        active
        message={typeof tour.step.message === "function" ? tour.step.message() : tour.step.message}
        position="auto"
        targetPadding={2}
        targetRadius={6}
        onDismiss={tour.dismiss}
        onNext={() => action.run({ tour, history })}
        onSkip={tour.skip}
        nextLabel={action.label}
      />
    );
  }
  return (
    <SpotlightOverlay
      targetSelector={tour.selector}
      active
      message={typeof tour.step.message === "function" ? tour.step.message() : tour.step.message}
      position={tour.step.position || "auto"}
      targetPadding={2}
      targetRadius={6}
      onDismiss={tour.dismiss}
      passThrough={!!tour.step.interactive}
      onTargetClick={info ? undefined : tour.step.actionRequired ? () => {} : () => setTimeout(tour.next, 150)}
      onNext={tour.step.actionRequired ? null : last || info ? tour.next : () => {
        setTimeout(tour.next, 150);
        const el = document.querySelector(tour.selector);
        if (el && !tour.step.isDone?.(el)) el.click();
      }}
      onSkip={tour.skip}
      nextLabel={last ? "Finish" : "Next"}
    />
  );
};
